import { NgClass, NgTemplateOutlet } from '@angular/common'
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
  Renderer2,
  signal,
  viewChild
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { QueuedJobsService } from '@app/v2/shared/services/queued-jobs.service'
import { QueuedJobModel } from '@app/v2/shared/types/queued-job.model'
import { QUEUED_JOB_ICONS, QueuedJobsMenuVariant } from '@app/v2/shared/types/queued-job.type'
import { AuthService } from '@auth/services/auth.service'
import { distinctUntilChanged, map, skip } from 'rxjs'
import { QueuedJobsIconComponent } from '../queued-jobs-icon/queued-jobs-icon.component'
import { QueuedJobsPanelComponent } from '../queued-jobs-panel/queued-jobs-panel.component'

const BODY_SCROLL_LOCK_CLASS = 'tw-overflow-hidden'

@Component({
  selector: 'app-queued-jobs-menu',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, SafeDomPipe, QueuedJobsIconComponent, QueuedJobsPanelComponent],
  templateUrl: './queued-jobs-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'tw-relative' }
})
export class QueuedJobsMenuComponent implements OnInit {
  public readonly variant = input<QueuedJobsMenuVariant>('desktop')

  private readonly _panel = viewChild(QueuedJobsPanelComponent)
  private readonly _triggerButton = viewChild<ElementRef<HTMLButtonElement>>('triggerButton')
  private readonly _closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeButton')

  public isOpen = signal<boolean>(false)
  public isDrawerContentMounted = signal<boolean>(false)
  public readonly icons = QUEUED_JOB_ICONS
  public readonly title = QueuedJobModel.PANEL_TITLE
  public readonly processingCount = inject(QueuedJobsService).processingCount
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly _renderer = inject(Renderer2)
  private readonly _authService = inject(AuthService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _injector = inject(Injector)
  private _removeDocumentListeners: Array<() => void> = []
  private _hasLockedBodyScroll = false

  ngOnInit(): void {
    this._authService.currentWorkspaceChange
      .pipe(
        map((workspace) => workspace?.id),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => this.close())

    this._destroyRef.onDestroy(() => {
      this._unlistenDocument()
      this._unlockBodyScroll()
    })
  }

  /**
   * Toggles the queued jobs menu.
   */
  public toggle(): void {
    if (this.isOpen()) this.close()
    else this._open()
  }

  /**
   * Closes the queued jobs menu.
   */
  public close(): void {
    if (!this.isOpen()) return
    this.isOpen.set(false)
    this._unlistenDocument()
    this._unlockBodyScroll()
    if (this.variant() === 'mobile') this._triggerButton()?.nativeElement.focus()
  }

  /**
   * Unmounts the drawer content once the mobile drawer has fully slid out.
   * @param event {TransitionEvent} - Transition end event from the drawer.
   */
  public onDrawerTransitionEnd(event: TransitionEvent): void {
    if (event.target !== event.currentTarget || this.isOpen()) return
    this.isDrawerContentMounted.set(false)
  }

  /**
   * Opens the queued jobs menu.
   */
  private _open(): void {
    this.isOpen.set(true)
    if (this.variant() === 'mobile') {
      // Reopened before the slide-out finished: the panel is still mounted, so refetch explicitly.
      if (this.isDrawerContentMounted()) this._panel()?.reload()
      else this.isDrawerContentMounted.set(true)
      this._lockBodyScroll()
      afterNextRender(() => this._closeButton()?.nativeElement.focus(), { injector: this._injector })
    }
    this._listenDocument()
  }

  /**
   * Closes on Escape, and on outside mousedown for the desktop dropdown; attached only while open.
   */
  private _listenDocument(): void {
    this._removeDocumentListeners = [
      this._renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape') this.close()
      })
    ]

    if (this.variant() === 'desktop') {
      this._removeDocumentListeners.push(
        this._renderer.listen('document', 'mousedown', (event: MouseEvent) => {
          if (!this._host.nativeElement.contains(event.target as Node)) this.close()
        })
      )
    }
  }

  private _unlistenDocument(): void {
    this._removeDocumentListeners.forEach((removeListener) => removeListener())
    this._removeDocumentListeners = []
  }

  /**
   * Locks body scroll unless another overlay already holds the lock.
   */
  private _lockBodyScroll(): void {
    if (document.body.classList.contains(BODY_SCROLL_LOCK_CLASS)) return
    this._renderer.addClass(document.body, BODY_SCROLL_LOCK_CLASS)
    this._hasLockedBodyScroll = true
  }

  /**
   * Releases the body scroll lock only if this instance applied it.
   */
  private _unlockBodyScroll(): void {
    if (!this._hasLockedBodyScroll) return
    this._hasLockedBodyScroll = false
    this._renderer.removeClass(document.body, BODY_SCROLL_LOCK_CLASS)
  }
}
